<template>
  <div>
    <main-large-dialog
      :isValid="isValid"
      :is-visible.sync="computedIsVisible"
      :loading="isLoading"
      :title="
        isCopy
          ? $i18n.t('dialog.title.copyDatasource', { datasource_name: datasourceMetadata.verbose_name })
          : $i18n.t('dialog.addDataSource')
      "
      min-body-height="65vh"
      dialog-width="80%"
      header-shadow
      disagree-text="dialog.disagree"
      no-paddings-x
      agree-text="dialog.agreeAdd"
      @agree="addDataSourceHandler"
    >
      <template #body>
        <v-col v-if="isLoaded" cols="12">
          <v-row no-gutters class="border-bottom pb-2">
            <v-col class="px-5">
              <v-form v-model="isValid" ref="form">
                <v-row v-if="isCopy" no-gutters class="mb-6">
                  <v-col cols="auto">
                    <v-radio-group v-model="copyType" class="mt-0" hide-details mandatory>
                      <v-row>
                        <v-col>
                          <v-radio value="withData">
                            <template #label>
                              <span class="text-body-2 text-left label-dark flex-nowrap" style="white-space: nowrap">
                                {{ $i18n.t('dialog.copyWithData') }}
                              </span>
                            </template>
                          </v-radio>
                        </v-col>
                        <v-col>
                          <v-radio value="withoutData">
                            <template #label>
                              <span class="text-body-2 text-left label-dark" style="white-space: nowrap">
                                {{ $i18n.t('dialog.copyWithoutData') }}
                              </span>
                            </template>
                          </v-radio>
                        </v-col>
                      </v-row>
                    </v-radio-group>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="5">
                    <v-row no-gutters align="center">
                      <v-col cols="5" class="text-left">
                        <span> {{ $i18n.t('dialog.name') }}: </span>
                      </v-col>
                      <v-spacer />
                      <v-col cols="7" class="dict-value">
                        <data-input
                          class="pl-4"
                          v-model="payload.datasource_verbose_name"
                          :data-type="{
                            name: 'text',
                            max_length: datasourceNameMaxLength,
                          }"
                          :rules="[v => !!v || $i18n.t('rules.required')]"
                        />
                      </v-col>
                    </v-row>
                    <v-row no-gutters align="baseline">
                      <v-col cols="5" class="text-left">
                        <span> {{ $i18n.t('dialog.datasourceDescription') }}: </span>
                        <italic-subheader translation-path="default.optional" />
                      </v-col>
                      <v-spacer />
                      <v-col cols="7" class="dict-value">
                        <data-input
                          class="pl-4"
                          v-model="payload.description"
                          :data-type="{ name: 'textarea' }"
                          rows="3"
                        />
                      </v-col>
                    </v-row>
                    <v-row no-gutters align="center">
                      <v-col cols="5" class="text-left" :class="{ 'text--disabled': isDataCopy }">
                        <span> {{ $i18n.t('dialog.geometryType') }}: </span>
                      </v-col>
                      <v-spacer />
                      <v-col cols="7" class="dict-value">
                        <data-input
                          class="pl-4"
                          v-model="payload.geometry_type"
                          :data-type="dataTypeSelect"
                          :disabled="isDataCopy"
                          :items="geomTypes"
                          :rules="[v => !!v || $i18n.t('rules.required')]"
                        />
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-form>
            </v-col>
          </v-row>
          <v-row no-gutters class="border-bottom">
            <v-col class="py-9 pl-5 manage-text text-left">
              {{ $i18n.t('dialog.manageNewDataSourceAttributes') }}
            </v-col>
          </v-row>
          <v-row no-gutters justify="center" align="center" class="py-3">
            <v-col
              class="d-flex justify-center align-center column-title"
              v-for="columnTitle of columnsTitles"
              :key="columnTitle.name"
            >
              <span>
                {{ $i18n.t(columnTitle.translatePath) }}
              </span>
              <v-tooltip bottom color="font" v-if="columnTitle.info">
                <template #activator="{ on }">
                  <v-icon v-on="on" class="pl-1" small :color="$_colors.primarylight">
                    mdi-information
                  </v-icon>
                </template>
                <span>
                  {{ $i18n.t(`${columnTitle.translatePath}Info`) }}
                </span>
              </v-tooltip>
              <v-tooltip v-if="columnTitle.name === 'dict'" color="font" bottom>
                <template #activator="{ on }">
                  <v-btn v-on="on" color="success" :ripple="false" width="auto" depressed icon @click="openDictDialog">
                    <v-icon small> mdi-plus-circle </v-icon>
                  </v-btn>
                </template>
                <span> {{ $i18n.t('dialog.addNewDict') }} </span>
              </v-tooltip>
            </v-col>
          </v-row>
          <span v-for="(attribute, idx) of payload.attributes" :key="idx">
            <v-row no-gutters align="center" class="py-2 border-top justify-space-between" style="height: 64px">
              <v-col class="column-element-text">
                {{ attribute.verbose_name }}
              </v-col>
              <v-col class="column-element-text">
                {{ attribute.name }}
              </v-col>
              <v-col class="column-element-text">
                {{
                  attribute.data_type
                    ? dataTypes.find(dT => dT.name === attribute.data_type.name).verbose_name
                    : attribute.data_type
                }}
                <span
                  v-if="attribute.data_type.expression"
                  style="font-style: italic; font-weight: 300; color: rgba(0,0,0,0.59); font-size: 12px"
                >
                  ({{ $_loweralize($i18n.t('default.virtual')) }})
                </span>
                <v-icon
                  color="primary"
                  size="20"
                  class="pl-1"
                  @click="openAttributeSettings(attribute, idx)"
                  :disabled="!attribute.data_type.name"
                >
                  mdi-cog
                </v-icon>
              </v-col>
              <v-col class="column-element-text">
                {{ attribute.dict_id ? dictionaries[attribute.dict_id].name : '' }}
              </v-col>
              <v-col class="column-element-text">
                {{
                  attribute.relation
                    ? computedRelations.find(
                        relation =>
                          relation.relationKey ===
                          `${attribute.relation.data_source}-${attribute.relation.attribute}-${attribute.relation.representation}`
                      ).name
                    : ''
                }}
              </v-col>
              <v-col class="column-element-text">
                {{
                  attribute.measurement_unit_id
                    ? flatMeasurementUnits.find(mU => mU.id === attribute.measurement_unit_id).name
                    : attribute.measurement_unit_id
                }}
              </v-col>
              <v-col>
                <span>
                  <v-icon
                    :style="
                      `cursor: ${isDataCopy && notDeletableAttributes.includes(attribute.name) ? 'default' : 'pointer'}`
                    "
                    :color="isDataCopy && notDeletableAttributes.includes(attribute.name) ? 'grey' : 'error'"
                    @click="deleteAttribute(attribute.name)"
                  >
                    mdi-close
                  </v-icon>
                </span>
              </v-col>
            </v-row>
          </span>
          <v-form v-model="isNewAttributeValid" ref="newAttributeForm">
            <v-row no-gutters justify="center" align="center" class="py-1 border-top">
              <v-col>
                <data-input
                  class="px-5 py-0 ma-0"
                  v-model="newAttribute.verbose_name"
                  :data-type="dataTypeText"
                  :disabled="isDataCopy"
                  :rules="[v => !!v || $i18n.t('rules.required')]"
                />
              </v-col>
              <v-col>
                <data-input
                  class="px-5 py-0 ma-0"
                  v-model="newAttribute.name"
                  :data-type="dataTypeText"
                  :disabled="isDataCopy"
                  :rules="[v => !!v || /^[a-zA-Z]+[\w\d]*$/.test(v || '') || $i18n.t('rules.incorrectValue')]"
                />
              </v-col>
              <v-col>
                <v-row no-gutters class="px-5">
                  <v-col style="min-width: calc(100% - 20px)" class="pr-1">
                    <data-input
                      :menu-props="{ offsetY: true, minWidth: '200px' }"
                      v-model="newAttribute.data_type.name"
                      :items="excludedDataTypes"
                      :disabled="isDataCopy"
                      item-text="verbose_name"
                      item-value="name"
                      @change="
                        newAttribute.dict_id = undefined;
                        newAttribute.relation = undefined;
                      "
                      :data-type="dataTypeSelect"
                      :rules="[v => !!v || $i18n.t('rules.required')]"
                    />
                  </v-col>
                  <v-col style="width: 20px" class="pt-2">
                    <v-icon
                      color="primary"
                      size="20"
                      @click="openAttributeSettings(newAttribute)"
                      :disabled="!newAttribute.data_type.name"
                    >
                      mdi-cog
                    </v-icon>
                  </v-col>
                </v-row>
              </v-col>
              <v-col>
                <v-tooltip bottom color="font" :disabled="!!newAttribute.data_type || isDataCopy">
                  <template #activator="{ on }">
                    <span v-on="on">
                      <data-input
                        :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                        v-model="newAttribute.dict_id"
                        :items="Object.values(dictionaries).filter(d => d.data_type === newAttribute.data_type.name)"
                        item-value="id"
                        item-text="name"
                        clearable
                        :disabled="!newAttribute.data_type || isDataCopy"
                        class="px-5"
                        :data-type="dataTypeSelect"
                      >
                      </data-input>
                    </span>
                  </template>
                  {{ $i18n.t('dialog.body.dataTypeDict') }}
                </v-tooltip>
              </v-col>
              <v-col>
                <v-tooltip bottom color="font" :disabled="!!newAttribute.data_type || isDataCopy">
                  <template #activator="{ on }">
                    <span v-on="on">
                      <data-input
                        :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                        v-model="newAttribute.relation"
                        :items="computedRelations.filter(r => r.data_type === newAttribute.data_type.name)"
                        item-value="relationKey"
                        item-text="name"
                        return-object
                        clearable
                        :disabled="!newAttribute.data_type || isDataCopy"
                        class="px-5"
                        :data-type="dataTypeSelect"
                      >
                      </data-input>
                    </span>
                  </template>
                  {{ $i18n.t('dialog.body.dataTypeRelation') }}
                </v-tooltip>
              </v-col>
              <v-col>
                <data-input
                  :menu-props="{ offsetY: true, minWidth: '300px', maxWidth: '300px' }"
                  :items="formattedMeasurementUnits"
                  item-text="text"
                  item-value="id"
                  v-model="newAttribute.measurement_unit_id"
                  clearable
                  class="px-5"
                  :disabled="isDataCopy"
                  :data-type="dataTypeSelect"
                  disabled-sorting
                />
              </v-col>
              <v-col>
                <v-icon
                  :style="`cursor: ${isNewAttributeValid && !isDataCopy ? 'pointer' : 'default'}`"
                  :color="isNewAttributeValid && !isDataCopy ? 'success' : 'grey'"
                  @click="pushAttribute"
                >
                  mdi-check
                </v-icon>
              </v-col>
            </v-row>
          </v-form>
          <data-source-attribute-settings-dialog
            v-if="attributeSettingsLoaded"
            :is-visible.sync="attributeSettingsVisible"
            :attribute.sync="currentAttribute"
            @input="onAttributeSettingsSave"
          />
        </v-col>
        <v-col v-else>
          <main-loader class="pt-10" />
        </v-col>
      </template>
    </main-large-dialog>
    <dictionaries-editing
      v-if="isDictDialogLoaded"
      :is-visible.sync="isDictDialogVisible"
      :current-dict="{
        name: '',
        colors: [],
        pictures: [],
        picturesUrls: [],
        values: [],
        isNew: true,
        data_type: 'text',
      }"
    />
  </div>
</template>
<script>
import { call, get } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
import MainLargeDialog from '@/components/MainLargeDialog';
import MainLoader from '@/components/MainLoader';
import ItalicSubheader from '@/components/ItalicSubheader';
export default {
  name: 'NewDataSourceDialog',
  components: {
    DataInput,
    DataSourceAttributeSettingsDialog: () => import('@/components/DataSourceAttributeSettingsDialog'),
    MainLargeDialog,
    MainLoader,
    ItalicSubheader,
    DictionariesEditing: () => import('@/components/DictionariesEditing'),
  },
  props: {
    isVisible: {
      type: Boolean,
      required: false,
    },
    isCopy: {
      type: Boolean,
      default: false,
    },
    datasourceMetadata: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    datasourceNameMaxLength: get('admin/settingsValues@datasource_name_max_length'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    dataTypes: get('layers/dataTypes'),
    relations: get('admin/allowedRelations'),
    isDataCopy() {
      return this.isCopy && this.copyType === 'withData';
    },
    computedRelations() {
      return this.relations.map(relation => {
        const { attribute, data_source, representation } = relation;
        const relationKey = `${data_source}-${attribute}-${representation}`;
        return { ...relation, relationKey };
      });
    },
    dictionaries: get('admin/dictionaries'),
    excludedDataTypes() {
      return this.dataTypes.filter(d => d.name !== 'list' && d.name !== 'geometric');
    },
    formattedMeasurementUnits() {
      let formattedMeasurementUnits = [];
      for (let measurementUnitGroup of Object.keys(this.measurementUnits)) {
        formattedMeasurementUnits.push({ header: measurementUnitGroup });
        formattedMeasurementUnits = [
          ...formattedMeasurementUnits,
          ...this.measurementUnits[measurementUnitGroup].map(mU => {
            return { id: mU.id, text: `${mU.name} [${mU.symbol}]` };
          }),
        ];
        formattedMeasurementUnits.push({ divider: true });
      }
      return formattedMeasurementUnits;
    },
    geomTypes() {
      return [
        // TODO [1]: this workaround have to be changed when DEV-8929 will be implemented
        ...(this.isDataCopy
          ? [
              {
                value: 'multipoint',
                text: this.$i18n.t('dialog.style.point'),
              },
              {
                value: 'multilinestring',
                text: this.$i18n.t('dialog.style.line'),
              },
            ]
          : []),
        {
          value: 'multipolygon',
          text: this.$i18n.t('dialog.style.multipolygon'),
        },
        {
          value: 'point',
          text: this.$i18n.t('dialog.style.point'),
        },
        {
          value: 'linestring',
          text: this.$i18n.t('dialog.style.line'),
        },
        {
          value: 'polygon',
          text: this.$i18n.t('dialog.style.polygon'),
        },
        {
          value: 'noGeometry',
          text: this.$i18n.t('dialog.style.noGeometry'),
        },
      ];
    },
    measurementUnits: get('layers/measurementUnits'),
    flatMeasurementUnits() {
      let flatMeasurementUnits = [];
      for (const mU of Object.values(this.measurementUnits)) {
        flatMeasurementUnits = flatMeasurementUnits.concat(mU);
      }
      return flatMeasurementUnits;
    },
  },
  data: () => ({
    columnsTitles: [
      {
        name: 'verbose_name',
        translatePath: 'dialog.attributeName',
      },
      {
        name: 'name',
        translatePath: 'dialog.systemName',
        info: true,
      },
      {
        name: 'dataType',
        translatePath: 'dialog.dataType',
        info: true,
      },
      {
        name: 'dict',
        translatePath: 'dialog.dictionary',
        info: true,
      },
      {
        name: 'relation',
        translatePath: 'dialog.relation',
        info: true,
      },
      {
        name: 'units',
        translatePath: 'dialog.units',
        info: true,
      },
      {
        name: 'options',
        translatePath: 'dialog.options',
      },
    ],
    dataTypeText: {
      name: 'text',
    },
    dataTypeSelect: {
      name: 'select',
    },
    copyType: 'withData',
    isLoaded: false,
    isNewAttributeValid: false,
    isLoading: false,
    isValid: false,
    newAttribute: { data_type: {} },
    payload: {},
    notDeletableAttributes: [],
    isDictDialogLoaded: false,
    isDictDialogVisible: false,

    attributeSettingsLoaded: false,
    attributeSettingsVisible: false,
    currentAttribute: {},
  }),
  methods: {
    addDataSource: call('layers/addDataSource'),
    getDataTypes: call('layers/getDataTypes'),
    copyDataSourceData: call('layers/copyDataSourceData'),
    getDicts: call('admin/getDictionaries'),
    getRelations: call('admin/getAllowedRelations'),
    getMeasurementUnits: call('layers/getMeasurementUnits'),
    deleteDataSource: call('layers/deleteDataSource'),
    async copyDataSourceHandler(newDatasourceName) {
      try {
        await this.copyDataSourceData({
          fromDataSource: this.datasourceMetadata.name,
          toDataSource: newDatasourceName,
        });
      } catch {
        try {
          await this.deleteDataSource(newDatasourceName);
        } catch {
          //
        }
      }
    },
    async addDataSourceHandler() {
      try {
        this.isLoading = true;
        const payload = JSON.parse(JSON.stringify(this.payload));
        if (payload.geometry_type === 'noGeometry') {
          payload.geometry_type = null;
        }
        if (!this.isCopy) payload.srid = this.$_config.defaultEpsg.split(':')[1];
        if (payload.attributes && payload.attributes.length > 0) {
          payload.attributes = payload.attributes.map(attr => {
            return { ...attr };
          });
        }
        const r = await this.addDataSource({ payload, skipMetadataParsing: this.isDataCopy });
        if (this.isDataCopy) await this.copyDataSourceHandler(r.datasource_metadata.name);
        this.isLoading = false;
        this.computedIsVisible = false;
      } catch {
        this.isLoading = false;
        this.computedIsVisible = false;
      }
    },
    deleteAttribute(name) {
      if (this.isDataCopy && this.notDeletableAttributes.includes(name)) return;
      const currentAttributes = JSON.parse(JSON.stringify(this.payload.attributes));
      const currentFilteredAttributes = currentAttributes.filter(attr => attr.name !== name);
      this.$set(this.payload, 'attributes', currentFilteredAttributes);
      this.$forceUpdate();
    },
    pushAttribute() {
      if (this.isDataCopy) return;
      if (!this.isNewAttributeValid) {
        return;
      }
      if (!this.payload.attributes) {
        this.payload.attributes = [];
      }
      let newAttribute = JSON.parse(JSON.stringify(this.newAttribute));
      if (newAttribute.relation) {
        const { attribute, data_source, representation } = newAttribute.relation;
        newAttribute.relation = { attribute, data_source, representation };
      }
      this.payload.attributes.push(newAttribute);
      this.newAttribute = { data_type: {} };
      this.$refs?.newAttributeForm?.resetValidation();
    },
    prepareCopyData(datasourceMetadata) {
      this.payload.attributes = [];
      this.notDeletableAttributes = [];
      this.$forceUpdate();
      if (Object.keys(datasourceMetadata).length === 0) return;
      const attributes = JSON.parse(JSON.stringify(datasourceMetadata)).attributes_schema.attributes;
      this.payload.srid = datasourceMetadata.srid;
      this.payload.geometry_type = datasourceMetadata.geometry_type;
      if (!datasourceMetadata.geometry_type) this.payload.geometry_type = 'noGeometry';
      attributes
        .filter(attr => !attr.predefined && !['id'].includes(attr.name) && attr.data_type.name !== 'geometric')
        .forEach(attr => {
          const { verbose_name, name, data_type, relation, measurement_unit, dict_id } = attr;
          delete data_type.scada3_display_attribute;
          this.notDeletableAttributes.push(name);
          this.payload.attributes.push({
            verbose_name,
            name,
            data_type,
            ...(dict_id && { dict_id }),
            ...(measurement_unit && { measurement_unit_id: measurement_unit.id }),
            ...(relation && { relation }),
          });
        });
    },
    async init() {
      try {
        await Promise.all([this.getDicts(), this.getRelations(), this.getDataTypes(), this.getMeasurementUnits()]);
        this.$watch(
          vm => vm.datasourceMetadata,
          nV => {
            this.prepareCopyData(nV);
          },
          {
            deep: true,
            immediate: true,
          }
        );
        this.isLoaded = true;
      } catch {
        this.isLoaded = true;
      }
    },
    openDictDialog() {
      this.isDictDialogLoaded = true;
      this.isDictDialogVisible = true;
    },
    openAttributeSettings(attribute = this.newAttribute || {}, index) {
      this.currentAttribute = {
        ...JSON.parse(JSON.stringify(attribute)),
        new: !index && index !== 0,
        index,
        isVirtual: Boolean(attribute.data_type?.expression || attribute.type === 'virtual'),
      };
      this.attributeSettingsLoaded = true;
      this.attributeSettingsVisible = true;
    },
    onAttributeSettingsSave(attribute) {
      if (this.currentAttribute.new) this.newAttribute = { ...this.newAttribute, ...attribute };
      else {
        this.$set(this.payload.attributes, this.currentAttribute.index, attribute);
      }
      this.attributeSettingsVisible = false;
    },
  },
  watch: {
    computedIsVisible(nV) {
      if (!nV) {
        this.notDeletableAttributes = [];
        this.payload = {};
        this.$refs?.newAttributeForm?.reset();
        this.$refs?.form?.reset();
        this.$forceUpdate();
      }
    },
    copyType(nV, oV) {
      if (this.isCopy && this.computedIsVisible) {
        if (nV === 'withData' && oV === 'withoutData') {
          this.prepareCopyData(this.datasourceMetadata);
        } else if (nV === 'withoutData') {
          // TODO [2]: this workaround have to be changed when DEV-8929 will be implemented
          if (this.payload.geometry_type && this.payload.geometry_type.startsWith('multi'))
            this.payload.geometry_type = this.payload.geometry_type.replace('multi', '');
        }
      }
    },
  },
  mounted() {
    this.init();
  },
};
</script>
<style lang="scss" scoped>
.column-title {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
}
.column-element-text {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;

  overflow: hidden;
}
.manage-text {
  font-family: Roboto;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
  color: #202124;
}
.border-bottom {
  @include border(bottom, 1px);
}
.border-top {
  @include border(top, 1px);
}
.v-btn::before {
  background-color: transparent;
}
</style>
